import * as Sentry from "@sentry/react";
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { StrictMode, useEffect, useMemo } from "react";
import createAppRouter from "./pages/router";
import { AuthProvider } from "./contexts/auth-provider/provider";
import { NuqsAdapter } from "nuqs/adapters/react-router/v7";
import { RouterProvider } from "react-router";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import ToastProvider from "./components/common/toast";
import { useDynamicFavicon } from "./hooks/useDynamicFavicon";
import { useDynamicTitle } from "./hooks/useDynamicTitle";
import { FeatureFlagKeys, useFeatureFlag } from "./hooks/use-feature-flag";
import { useAuthorization } from "./hooks/use-authorization";

declare global {
  interface Window {
    __version?: string;
  }
}

const handleQueryError = (error: unknown) => {
  const status = (error as { response?: { status?: number } })?.response
    ?.status;
  if (status && status < 500) {
    return;
  }
  Sentry.captureException(error);
};

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: handleQueryError,
  }),
  mutationCache: new MutationCache({
    onError: handleQueryError,
  }),
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 2,
    },
  },
});

// Componente interno para usar hooks que requieren QueryClient
const AppContent = () => {
  useDynamicFavicon();
  useDynamicTitle();
  const commit = import.meta.env.VITE_APP_COMMIT;
  const tag = import.meta.env.VITE_APP_TAG;
  const message = `${commit}`.slice(0, 7) + (tag ? ` (${tag})` : "").trim();
  const isSmsEnabled = useFeatureFlag(FeatureFlagKeys.ALLOW_SMS).isEnabled;
  const { hasPermission } = useAuthorization();
  const router = useMemo(
    () => createAppRouter(queryClient, isSmsEnabled, hasPermission),
    [isSmsEnabled, hasPermission],
  );

  useEffect(() => {
    if (globalThis.window !== undefined) {
      globalThis.window.__version = message;
    }
  }, [message]);

  return (
    <NuqsAdapter>
      <RouterProvider router={router} />
      <ToastProvider />
      <ReactQueryDevtools initialIsOpen={false} />
    </NuqsAdapter>
  );
};

const App = () => {
  return (
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </QueryClientProvider>
    </StrictMode>
  );
};

export default App;
